import React, {
  useCallback,
  useEffect,
  useMemo,
  useState,
} from 'react';

import {
  Navigate,
  useNavigate,
  useSearchParams,
} from 'react-router-dom';

import { supabase } from './supabaseClient';
import { useSession } from './SessionContext';

import TechLogReviewWorkspace from './techlog/TechLogReviewWorkspace';

import {
  fetchOfficialTechLogsForBookings,
  indexOfficialTechLogs,
  saveOfficialTechLogEntry,
} from './officialTechLog';

import {
  getAircraftAnnualCheckDate,
  setAircraftAnnualCheckDate,
} from './aircraftAnnualCheckStore';
import { bookingUpdateContainsId } from './booking/bookingUtils';

function normalizeRole(...values) {
  for (const value of values) {
    const role = String(value || '')
      .trim()
      .toLowerCase();

    if (role) return role;
  }

  return '';
}

function isOwnerByEmail(email) {
  return (
    String(email || '')
      .trim()
      .toLowerCase() ===
    'owner@demo.wingit.app'
  );
}

function formatDate(value) {
  const text = String(value || '').trim();

  if (!text) return '';

  const match = text.match(
    /^(\d{4})-(\d{2})-(\d{2})$/
  );

  if (match) {
    return `${match[3]}/${match[2]}/${match[1]}`;
  }

  return text;
}

function formatAirfields(entry) {
  const departure = String(
    entry?.departureAirfield || ''
  )
    .trim()
    .toUpperCase();

  const arrival = String(
    entry?.arrivalAirfield || ''
  )
    .trim()
    .toUpperCase();

  if (!departure && !arrival) {
    return '';
  }

  if (
    departure &&
    arrival &&
    departure === arrival
  ) {
    return `Local · ${departure}`;
  }

  return `${departure || '-'} / ${
    arrival || '-'
  }`;
}

function getAnnualCountdownDays(dateValue) {
  if (!dateValue) return null;

  const due = new Date(
    `${dateValue}T00:00:00`
  );

  if (!Number.isFinite(due.getTime())) {
    return null;
  }

  const now = new Date();

  const today = new Date(
    now.getFullYear(),
    now.getMonth(),
    now.getDate()
  );

  const difference =
    due.getTime() - today.getTime();

  return Math.ceil(
    difference / (1000 * 60 * 60 * 24)
  );
}

function createTechLogPayload(
  booking,
  draft,
  pilot
) {
  const defect = String(
    draft?.defect || 'Nil'
  ).trim();

  const defectReported =
    draft?.aircraftStatus ===
      'defect_reported' ||
    (defect &&
      defect.toLowerCase() !== 'nil');

  return {
    flightNumber:
      draft?.flightNumber ||
      String(booking.id),

    flightDate: booking.date || '',

    pilotName:
      pilot?.full_name ||
      pilot?.email ||
      'Unknown pilot',

    departureAirfield:
      draft?.departureAirfield || '',

    arrivalAirfield:
      draft?.arrivalAirfield || '',

    departureTime:
      draft?.departureTime ||
      booking.time ||
      '',

    arrivalTime:
      draft?.arrivalTime ||
      booking.end_time ||
      '',

    flightTime:
      draft?.flightTime || '',

    fuelUpliftLitres:
      draft?.fuelUpliftLitres || '',

    oilUpliftLitres:
      draft?.oilUpliftLitres || '',

    signature:
      draft?.signature || '',

    aircraftStatus: defectReported
      ? 'defect_reported'
      : 'serviceable',

    serviceable: !defectReported,

    defect: defect || 'Nil',
    snag: defect || 'Nil',

    defectStatus: defectReported
      ? 'open'
      : 'none',

    defectReportedAt: defectReported
      ? new Date().toISOString()
      : '',

    defectReportedBy: defectReported
      ? pilot?.full_name ||
        pilot?.email ||
        ''
      : '',

    remarks: draft?.remarks || '',
    notes: draft?.remarks || '',
  };
}

export default function TechLogReviewPage() {
  const session = useSession();
  const user = session?.user;

  const navigate = useNavigate();
  const [searchParams] = useSearchParams();

  const requestedAircraftId = String(
    searchParams.get('aircraftId') || ''
  ).trim();

  const requestedBookingId = String(
    searchParams.get('bookingId') || ''
  ).trim();

  const [resolvedRole, setResolvedRole] =
    useState(
      normalizeRole(
        user?.user_metadata?.role,
        user?.app_metadata?.role,
        user?.role
      )
    );

  const [loading, setLoading] =
    useState(true);

  const [errorMessage, setErrorMessage] =
    useState('');

  const [aircraft, setAircraft] =
    useState(null);

  const [bookings, setBookings] =
    useState([]);

  const [userMap, setUserMap] =
    useState({});

  const [techLogMap, setTechLogMap] =
    useState({});

  const [
    techLogVersionMap,
    setTechLogVersionMap,
  ] = useState({});

  const [
    saveNoticeMap,
    setSaveNoticeMap,
  ] = useState({});

  const [
    annualCheckDate,
    setAnnualCheckDateState,
  ] = useState('');

  const isOwner = useMemo(() => {
    const roles = [
      resolvedRole,
      user?.user_metadata?.role,
      user?.app_metadata?.role,
      user?.role,
    ]
      .map((value) =>
        String(value || '')
          .trim()
          .toLowerCase()
      )
      .filter(Boolean);

    return (
      roles.includes('owner') ||
      isOwnerByEmail(user?.email)
    );
  }, [
    resolvedRole,
    user?.user_metadata?.role,
    user?.app_metadata?.role,
    user?.role,
    user?.email,
  ]);

  useEffect(() => {
    let cancelled = false;

    async function resolveRole() {
      if (!user?.id) return;

      const { data } = await supabase
        .from('users')
        .select('role')
        .eq('id', user.id)
        .maybeSingle();

      if (!cancelled) {
        setResolvedRole(
          normalizeRole(
            data?.role,
            user?.user_metadata?.role,
            user?.app_metadata?.role,
            user?.role
          )
        );
      }
    }

    resolveRole();

    return () => {
      cancelled = true;
    };
  }, [
    user?.id,
    user?.user_metadata?.role,
    user?.app_metadata?.role,
    user?.role,
  ]);

  const loadWorkspace =
    useCallback(async () => {
      if (
        !user?.id ||
        !isOwner ||
        !requestedAircraftId
      ) {
        setLoading(false);
        return;
      }

      const organisationId = String(
        session?.profile?.organisation_id ||
          ''
      ).trim();

      if (!organisationId) {
        setErrorMessage(
          'Could not identify the current organisation.'
        );
        setLoading(false);
        return;
      }

      setLoading(true);
      setErrorMessage('');

      const [
        aircraftResult,
        bookingResult,
      ] = await Promise.all([
        supabase
          .from('aircraft')
          .select(
            'id, registration, icao_type, recording_method'
          )
          .eq('organisation_id', organisationId)
          .eq('id', requestedAircraftId)
          .maybeSingle(),

        supabase
          .from('bookings')
          .select(
            'id, user_id, aircraft_id, date, time, end_date, end_time, duration, purpose, briefing_data, tech_log_status, tech_log_completed_at'
          )
          .eq('aircraft_id', requestedAircraftId)
          .order('date', {
            ascending: false,
          })
          .order('time', {
            ascending: false,
          }),
      ]);

      if (aircraftResult.error) {
        setErrorMessage(
          'Could not load this aircraft.'
        );
        setLoading(false);
        return;
      }

      if (bookingResult.error) {
        setErrorMessage(
          'Could not load the aircraft flight history.'
        );
        setLoading(false);
        return;
      }

      const bookingRows =
        bookingResult.data || [];

      const userIds = [
        ...new Set(
          bookingRows
            .map((booking) => booking.user_id)
            .filter(Boolean)
        ),
      ];

      let nextUserMap = {};

      if (userIds.length > 0) {
        const { data: users } =
          await supabase
            .from('users')
            .select(
              'id, full_name, email, phone'
            )
            .in('id', userIds);

        nextUserMap = (users || []).reduce(
          (map, member) => {
            map[String(member.id)] = member;
            return map;
          },
          {}
        );
      }

      const bookingIds = bookingRows.map(
        (booking) => String(booking.id)
      );

      const { data: logRows, error: logError } =
        await fetchOfficialTechLogsForBookings(
          bookingIds
        );

      if (logError) {
        setErrorMessage(
          'Could not load the live aircraft tech log.'
        );
        setLoading(false);
        return;
      }

      const indexed =
        indexOfficialTechLogs(logRows || []);

      setAircraft(
        aircraftResult.data || null
      );

      setBookings(bookingRows);
      setUserMap(nextUserMap);

      setTechLogMap(
        indexed.logMap || {}
      );

      setTechLogVersionMap(
        indexed.versionMap || {}
      );

      setAnnualCheckDateState(
        getAircraftAnnualCheckDate(
          requestedAircraftId
        )
      );

      setLoading(false);
    }, [
      user?.id,
      isOwner,
      requestedAircraftId,
      session?.profile?.organisation_id,
    ]);

  useEffect(() => {
    loadWorkspace();
  }, [loadWorkspace]);

  const outstandingBookings = useMemo(
    () =>
      bookings.filter(
        (booking) =>
          String(
            booking.tech_log_status || ''
          ).toLowerCase() !== 'completed'
      ),
    [bookings]
  );

  const acceptedBookings = useMemo(
    () =>
      bookings.filter((booking) => {
        const entry =
          techLogMap[String(booking.id)];

        return (
          entry &&
          typeof entry === 'object' &&
          Object.keys(entry).length > 0
        );
      }),
    [bookings, techLogMap]
  );

  const annualCountdownDays = useMemo(
    () =>
      getAnnualCountdownDays(
        annualCheckDate
      ),
    [annualCheckDate]
  );

  function showNotice(
    bookingId,
    message
  ) {
    const key = `${bookingId}:tech`;

    setSaveNoticeMap((previous) => ({
      ...previous,
      [key]: message,
    }));

    window.setTimeout(() => {
      setSaveNoticeMap((previous) => {
        const next = { ...previous };
        delete next[key];
        return next;
      });
    }, 3000);
  }

  async function saveDraft(
    booking,
    draft
  ) {
    const bookingKey = String(booking.id);
    const pilot =
      userMap[String(booking.user_id)];

    const entry = createTechLogPayload(
      booking,
      draft,
      pilot
    );

    const result =
      await saveOfficialTechLogEntry({
        bookingId: bookingKey,
        userId: user?.id,
        entry,
        expectedVersion:
          techLogVersionMap[bookingKey] ?? 0,
      });

    if (!result.ok) {
      showNotice(
        booking.id,
        result.message ||
          'Could not save the draft.'
      );

      return false;
    }

    setTechLogMap((previous) => ({
      ...previous,
      [bookingKey]: entry,
    }));

    setTechLogVersionMap((previous) => ({
      ...previous,
      [bookingKey]: result.version,
    }));

    showNotice(
      booking.id,
      'Draft saved.'
    );

    return true;
  }

  async function acceptEntry(
    booking,
    draft
  ) {
    const saved = await saveDraft(
      booking,
      draft
    );

    if (!saved) return;

    const completedAt =
      new Date().toISOString();

    const { data: acceptedRows, error } = await supabase
      .from('bookings')
      .update({
        tech_log_status: 'completed',
        tech_log_completed_at:
          completedAt,
      })
      .eq('id', booking.id)
      .select('id');

    if (error || !bookingUpdateContainsId(acceptedRows, booking.id)) {
      showNotice(
        booking.id,
        'Entry saved, but the outstanding item could not be closed.'
      );

      return;
    }

    setBookings((previous) =>
      previous.map((row) =>
        String(row.id) ===
        String(booking.id)
          ? {
              ...row,
              tech_log_status:
                'completed',
              tech_log_completed_at:
                completedAt,
            }
          : row
      )
    );

    showNotice(
      booking.id,
      'Accepted into live tech log.'
    );
  }

  function handleAnnualDateChange(value) {
    setAnnualCheckDateState(value);

    setAircraftAnnualCheckDate(
      requestedAircraftId,
      value
    );
  }

  function handleExport() {
    navigate(
      `/aircraft-tech-log?aircraftId=${requestedAircraftId}&from=dashboard`
    );
  }

  const liveLog = (
    <LiveTechLogTable
      bookings={acceptedBookings}
      userMap={userMap}
      techLogMap={techLogMap}
    />
  );

  if (!loading && !isOwner) {
    return (
      <Navigate
        to="/dashboard"
        replace
      />
    );
  }

  return (
    <div style={styles.page}>
      <div style={styles.topBar}>
        <button
          type="button"
          onClick={() =>
            navigate('/dashboard')
          }
          style={styles.backButton}
        >
          Back to Dashboard
        </button>
      </div>

      {loading ? (
        <div style={styles.message}>
          Loading Tech Log workspace...
        </div>
      ) : null}

      {!loading && errorMessage ? (
        <div style={styles.error}>
          {errorMessage}
        </div>
      ) : null}

      {!loading &&
      !errorMessage &&
      aircraft ? (
        <TechLogReviewWorkspace
          aircraft={aircraft}
          bookings={outstandingBookings}
          userMap={userMap}
          techLogMap={techLogMap}
          selectedBookingId={
            requestedBookingId
          }
          saveNoticeMap={saveNoticeMap}
          onSaveDraft={saveDraft}
          onAcceptEntry={acceptEntry}
          onExport={handleExport}
          annualCheckDate={annualCheckDate}
          annualCountdownDays={
            annualCountdownDays
          }
          onAnnualCheckDateChange={
            handleAnnualDateChange
          }
          liveLog={liveLog}
        />
      ) : null}
    </div>
  );
}

function LiveTechLogTable({
  bookings,
  userMap,
  techLogMap,
}) {
  if (bookings.length === 0) {
    return (
      <div style={styles.emptyLog}>
        No accepted tech log entries yet.
      </div>
    );
  }

  return (
    <table style={styles.table}>
      <thead>
        <tr>
          <th style={styles.th}>Flight</th>
          <th style={styles.th}>Date</th>
          <th style={styles.th}>Pilot</th>
          <th style={styles.th}>Route</th>
          <th style={styles.th}>Off</th>
          <th style={styles.th}>On</th>
          <th style={styles.th}>Hours</th>
          <th style={styles.th}>Fuel</th>
          <th style={styles.th}>Oil</th>
          <th style={styles.th}>Defect</th>
          <th style={styles.th}>Remarks</th>
        </tr>
      </thead>

      <tbody>
        {bookings.map((booking) => {
          const entry =
            techLogMap[String(booking.id)] ||
            {};

          const pilot =
            userMap[String(booking.user_id)];

          return (
            <tr key={booking.id}>
              <td style={styles.td}>
                {entry.flightNumber ||
                  booking.id}
              </td>

              <td style={styles.td}>
                {formatDate(
                  entry.flightDate ||
                    booking.date
                )}
              </td>

              <td style={styles.td}>
                {entry.pilotName ||
                  pilot?.full_name ||
                  pilot?.email ||
                  '-'}
              </td>

              <td style={styles.td}>
                {formatAirfields(entry)}
              </td>

              <td style={styles.td}>
                {entry.departureTime || '-'}
              </td>

              <td style={styles.td}>
                {entry.arrivalTime || '-'}
              </td>

              <td style={styles.td}>
                {entry.flightTime || '-'}
              </td>

              <td style={styles.td}>
                {entry.fuelUpliftLitres ||
                  '0'}
              </td>

              <td style={styles.td}>
                {entry.oilUpliftLitres ||
                  '0'}
              </td>

              <td style={styles.td}>
                {entry.defect ||
                  entry.snag ||
                  'Nil'}
              </td>

              <td style={styles.td}>
                {entry.remarks ||
                  entry.notes ||
                  '-'}
              </td>
            </tr>
          );
        })}
      </tbody>
    </table>
  );
}

const styles = {
  page: {
    maxWidth: 1180,
    margin: '2rem auto',
    padding: '0 1rem 2rem',
  },

  topBar: {
    marginBottom: '0.75rem',
  },

  backButton: {
    background: 'transparent',
    color: '#b6eaff',
    border:
      '1px solid rgba(120,180,255,0.24)',
    borderRadius: 8,
    padding: '0.5rem 0.75rem',
    cursor: 'pointer',
  },

  message: {
    color: '#b6eaff',
    padding: '1rem',
  },

  error: {
    color: '#ff9c9c',
    background:
      'rgba(255,156,156,0.08)',
    border:
      '1px solid rgba(255,156,156,0.24)',
    borderRadius: 10,
    padding: '0.8rem',
  },

  emptyLog: {
    color: '#475569',
    padding: '1rem',
    fontSize: '0.86rem',
  },

  table: {
    width: '100%',
    minWidth: 1180,
    borderCollapse: 'collapse',
    background: '#ffffff',
    fontFamily:
      'Arial, Helvetica, sans-serif',
  },

  th: {
    color: '#111827',
    background: '#e5e7eb',
    border: '1px solid #374151',
    padding: '0.4rem',
    textAlign: 'left',
    fontSize: '0.7rem',
  },

  td: {
    color: '#111827',
    background: '#ffffff',
    border: '1px solid #374151',
    padding: '0.38rem',
    fontSize: '0.76rem',
    verticalAlign: 'top',
  },
};